import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface DashboardShellState {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  isMobile: boolean;
}

const DashboardShellContext = createContext<DashboardShellState | null>(null);
const MOBILE_BREAKPOINT = 1024;

function useProvideDashboardShell(): DashboardShellState {
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const updateMatch = () => {
      const mobile = mediaQuery.matches;
      setIsMobile(mobile);
      if (!mobile) {
        setDrawerOpen(false);
      }
    };

    updateMatch();
    mediaQuery.addEventListener("change", updateMatch);

    return () => mediaQuery.removeEventListener("change", updateMatch);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  return useMemo(
    () => ({ isSidebarCollapsed, toggleSidebar, isDrawerOpen, openDrawer, closeDrawer, isMobile }),
    [isSidebarCollapsed, toggleSidebar, isDrawerOpen, openDrawer, closeDrawer, isMobile]
  );
}

export function DashboardShellProvider({ children }: { children: ReactNode }) {
  const value = useProvideDashboardShell();
  return <DashboardShellContext.Provider value={value}>{children}</DashboardShellContext.Provider>;
}

export function useDashboardShell(): DashboardShellState {
  const context = useContext(DashboardShellContext);
  if (!context) {
    throw new Error("useDashboardShell must be used within a DashboardShellProvider");
  }
  return context;
}
